import {
  type OrderSetReadDto,
  useDeviceDefinitionsResult,
  useDiagnosticDefinitionsResult,
  useExamDefinitions,
  useProtocolDefinitionsResult,
  useTreatmentDefinitionsResult,
} from "@repo/data-store";
import {
  type PresentedOrderSetDto,
  type PresentedOrderSetsState,
  resolveOrderSetPresentations,
} from "./order-set-presentation";

/** Provider-owned composition of aggregate state with independently reactive catalog views. */
export function usePresentedOrderSets(orderSets: readonly OrderSetReadDto[]): PresentedOrderSetsState {
  const protocolCanonicals = [
    ...new Set(orderSets.flatMap((set) => (set.protocol ? [`${set.protocol.url}|${set.protocol.version}`] : []))),
  ];
  const definitionCanonicals = [
    ...new Set(
      orderSets.flatMap((set) => set.orders.map((order) => `${order.definition.url}|${order.definition.version}`)),
    ),
  ];
  const protocols = useProtocolDefinitionsResult({ canonicals: protocolCanonicals });
  const treatments = useTreatmentDefinitionsResult({ canonicals: definitionCanonicals });
  const diagnostics = useDiagnosticDefinitionsResult({ canonicals: definitionCanonicals });
  // Requirements belong to the ordered revision; names still follow the lineage.
  const exams = useExamDefinitions();
  // Device requirements are unpinned by design, so the current revision of every
  // kind is what a procedure inherits — there is no canonical to filter by.
  const devices = useDeviceDefinitionsResult();
  return resolveOrderSetPresentations(orderSets, {
    protocols: protocols.definitions,
    treatments: treatments.definitions,
    diagnostics: diagnostics.definitions,
    exams,
    devices: devices.definitions,
    isLoading: protocols.isLoading || treatments.isLoading || diagnostics.isLoading || devices.isLoading,
    isReady: protocols.isReady && treatments.isReady && diagnostics.isReady && devices.isReady,
  });
}

export type PresentedOrderSetState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "missing"; missingCanonicals: readonly string[] }
  | { status: "ready"; data: PresentedOrderSetDto };

export function usePresentedOrderSet(orderSet: OrderSetReadDto | undefined): PresentedOrderSetState {
  const state = usePresentedOrderSets(orderSet ? [orderSet] : []);
  if (!orderSet) return { status: "idle" };
  if (state.status !== "ready") return state;
  const [data] = state.data;
  if (data) return { status: "ready", data };
  const [unavailable] = state.unavailable;
  return unavailable ? { status: "missing", missingCanonicals: unavailable.missingCanonicals } : { status: "idle" };
}
